// AdminAppRagPage — Phase 6 · RAG debug (verified PDF corpus for AI generation).
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Database, Upload, Search, Trash2, ChevronDown, ChevronRight, Loader2,
  AlertCircle, FileText, RefreshCw, CheckCircle,
} from "lucide-react";
import { adminApi } from "@/lib/adminApi";

const AdminAppRagPage = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [chunksByDoc, setChunksByDoc] = useState({});
  const [query, setQuery] = useState("");
  const [searchK, setSearchK] = useState(5);
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedName, setUploadedName] = useState("");
  const [themeSlug, setThemeSlug] = useState("");
  const fileRef = useRef(null);

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.listRagDocs();
      const list = Array.isArray(res) ? res : res?.items || res?.docs || [];
      setDocs(list);
    } catch (e) {
      setError(e?.message || String(e));
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    setUploadedName("");
    try {
      const res = await adminApi.ragUpload(file, { theme_slug: themeSlug.trim() || undefined });
      const label = res?.filename
        ? `${res.filename} (${res.format?.toUpperCase()} · ${res.extracted_chars} car.${themeSlug ? ` · thème: ${themeSlug}` : ""})`
        : res?.doc_id || file.name;
      setUploadedName(label);
      fetchDocs();
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDelete = async (doc) => {
    const id = doc.doc_id || doc.id || doc._id;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Supprimer "${doc.title || doc.filename || id}" de l'index RAG ?`)) return;
    try {
      await adminApi.ragDeleteDoc(id);
      setDocs((prev) => prev.filter((d) => (d.doc_id || d.id || d._id) !== id));
    } catch (e) { setError(e?.message || String(e)); }
  };

  const toggleExpand = async (doc) => {
    const id = doc.doc_id || doc.id || doc._id;
    const willOpen = !expanded[id];
    setExpanded({ ...expanded, [id]: willOpen });
    if (willOpen && !chunksByDoc[id]) {
      try {
        const res = await adminApi.ragChunks(id);
        setChunksByDoc({ ...chunksByDoc, [id]: Array.isArray(res) ? res : res?.chunks || res?.items || [] });
      } catch (e) {
        setChunksByDoc({ ...chunksByDoc, [id]: [] });
        setError(e?.message || String(e));
      }
    }
  };

  const handleSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    setSearchResults(null);
    try {
      const res = await adminApi.ragSearch(query, searchK);
      setSearchResults(Array.isArray(res) ? res : res?.results || res?.chunks || []);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setSearching(false);
    }
  };

  return (
    <div data-testid="admin-app-rag">
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)]">RAG — corpus IA</h1>
        <button onClick={fetchDocs} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[4px] border border-[color:var(--gv-line)] bg-white text-[13px]" data-testid="rag-refresh-btn">
          <RefreshCw size={13} />
        </button>
      </div>
      <p className="text-sm text-[color:var(--gv-ink)]/60 mb-6">
        Vos documents vérifiés (PDF, DOCX, MD, TXT) alimentent le RAG utilisé par l'IA pour générer les fiches Atlas et les parcours Academy.
        L'IA cite vos sources au lieu d'inventer.
      </p>

      {/* Upload zone */}
      <div className="bg-white border border-dashed border-[color:var(--gv-line)] rounded-[5px] p-6 mb-6 flex flex-col items-center" data-testid="rag-upload-zone">
        <Upload size={28} className="text-[color:var(--gv-pink)] mb-2" />
        <div className="text-[13px] text-[color:var(--gv-ink)]/70 mb-3">
          Glisser un fichier ou cliquer pour uploader (PDF, DOCX, CSV, MD, TXT — max 15 Mo)
        </div>
        <input
          type="text"
          value={themeSlug}
          onChange={(e) => setThemeSlug(e.target.value)}
          placeholder="Thème à lier (slug optionnel, ex: cremant-de-bourgogne)"
          className="w-full max-w-md mb-3 px-3 py-2 rounded-[4px] border border-[color:var(--gv-line)] text-[12.5px]"
          data-testid="rag-upload-theme-slug"
        />
        <input ref={fileRef} type="file" accept=".pdf,.docx,.csv,.md,.txt" onChange={handleUpload} className="hidden" data-testid="rag-upload-input" />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[4px] bg-[color:var(--gv-pink)] text-white text-[13px] hover:bg-[color:var(--gv-pink-dark,#C01661)] disabled:opacity-50"
          data-testid="rag-upload-btn"
        >
          {uploading ? <><Loader2 size={13} className="animate-spin" /> Parsing + indexation…</> : <><Upload size={13} /> Choisir un fichier</>}
        </button>
        {uploadedName && (
          <div className="mt-3 text-[12.5px] text-emerald-700 inline-flex items-center gap-1" data-testid="rag-upload-success">
            <CheckCircle size={12} /> Indexé : {uploadedName}
          </div>
        )}
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-[5px] p-4 text-sm text-rose-700 flex items-start gap-2 mb-4" data-testid="rag-error">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">Erreur RAG</div>
            <div className="text-[12px] opacity-70 mt-1">{error}</div>
          </div>
        </div>
      )}

      {/* Search box */}
      <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-4 mb-6" data-testid="rag-search-box">
        <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-2">Recherche sémantique de test</div>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--gv-ink)]/40" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
              placeholder="Ex : Quelles sont les caractéristiques des grands crus de Chablis ?"
              className="gv-input w-full pl-8"
              data-testid="rag-search-input"
            />
          </div>
          <input
            type="number"
            value={searchK}
            onChange={(e) => setSearchK(parseInt(e.target.value || "5", 10))}
            min={1}
            max={20}
            className="gv-input w-16 text-center"
            data-testid="rag-search-k"
            title="Nombre de résultats"
          />
          <button
            onClick={handleSearch}
            disabled={searching || !query.trim()}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[4px] bg-[color:var(--gv-ink)] text-white text-[13px] disabled:opacity-50"
            data-testid="rag-search-btn"
          >
            {searching ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />} Chercher
          </button>
        </div>
        {searchResults !== null && (
          <div className="mt-3" data-testid="rag-search-results">
            {searchResults.length === 0 ? (
              <div className="text-[12.5px] text-[color:var(--gv-ink)]/55">Aucun résultat pertinent trouvé.</div>
            ) : (
              <ul className="space-y-2">
                {searchResults.map((r, i) => (
                  <li key={i} className="text-[12.5px] bg-[color:var(--gv-mist)]/50 rounded-[4px] p-3">
                    <div className="flex justify-between mb-1 text-[11px] text-[color:var(--gv-ink)]/55">
                      <span>{r.doc_title || r.source || r.filename || "—"}</span>
                      {r.score !== undefined && <span>score : {typeof r.score === "number" ? r.score.toFixed(3) : r.score}</span>}
                    </div>
                    <div className="text-[color:var(--gv-ink)]/85 leading-relaxed">{r.text || r.content || r.chunk}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Docs list */}
      <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] overflow-hidden">
        {loading ? (
          <div className="p-8 text-sm text-[color:var(--gv-ink)]/55 flex items-center gap-2" data-testid="rag-loading">
            <Loader2 size={14} className="animate-spin" /> Chargement…
          </div>
        ) : error && docs.length === 0 ? null : docs.length === 0 ? (
          <div className="p-8 text-sm text-[color:var(--gv-ink)]/55 flex items-center gap-2" data-testid="rag-empty">
            <Database size={14} /> Aucun document indexé — uploadez votre premier PDF pour commencer.
          </div>
        ) : (
          <ul>
            {docs.map((d) => {
              const id = d.doc_id || d.id || d._id;
              const isOpen = !!expanded[id];
              return (
                <li key={id} className="border-b border-[color:var(--gv-line)] last:border-b-0" data-testid={`rag-doc-${id}`}>
                  <div className="flex items-center gap-3 px-4 py-3 hover:bg-[color:var(--gv-mist)]/40">
                    <button onClick={() => toggleExpand(d)} className="text-[color:var(--gv-ink)]/50">
                      {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>
                    <FileText size={14} className="text-[color:var(--gv-pink)]" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[color:var(--gv-ink)] truncate">{d.title || d.filename || id}</div>
                      <div className="text-[11.5px] text-[color:var(--gv-ink)]/55 truncate">
                        {(d.chunks_count ?? d.chunk_count ?? "?")} chunks
                        {d.size_bytes ? ` · ${(d.size_bytes / 1024).toFixed(1)} Ko` : ""}
                        {d.created_at ? ` · ${new Date(d.created_at).toLocaleDateString("fr-FR")}` : ""}
                      </div>
                    </div>
                    <button onClick={() => handleDelete(d)} className="p-1.5 rounded hover:bg-red-50 text-[color:var(--gv-ink)]/40 hover:text-red-600" title="Supprimer" data-testid={`rag-delete-${id}`}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                  {isOpen && (
                    <div className="px-4 pb-3 pt-1 bg-[color:var(--gv-mist)]/30">
                      {chunksByDoc[id] === undefined ? (
                        <div className="text-[12px] text-[color:var(--gv-ink)]/55 flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> Chargement chunks…</div>
                      ) : chunksByDoc[id].length === 0 ? (
                        <div className="text-[12px] text-[color:var(--gv-ink)]/55">Aucun chunk indexé.</div>
                      ) : (
                        <ul className="space-y-2">
                          {chunksByDoc[id].map((c, i) => (
                            <li key={i} className="text-[11.5px] bg-white border border-[color:var(--gv-line)] rounded-[3px] p-2 leading-relaxed text-[color:var(--gv-ink)]/80" data-testid={`rag-chunk-${id}-${i}`}>
                              <span className="text-[color:var(--gv-ink)]/40 mr-2">#{i + 1}</span>
                              {c.text || c.content || c.chunk || JSON.stringify(c).slice(0, 200)}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminAppRagPage;
